import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

import { AdServerAuthProvider, ApiClientBridge } from './app/auth';
import { ToastProvider } from './app/toast';
import { router } from './app/router';
import './index.css';

/**
 * Server state belongs to TanStack Query; nothing here duplicates it in a global store.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Figures an advertiser is billed on should not be stale for long, but every focus change
      // triggering a refetch would hammer the API for no benefit.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A 401 is handled by the client, which restarts the login flow; retrying it would just
      // queue more failures behind a redirect that is already happening.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AdServerAuthProvider>
      <ApiClientBridge>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </QueryClientProvider>
      </ApiClientBridge>
    </AdServerAuthProvider>
  </StrictMode>,
);
